import React, { useMemo } from 'react'
import propTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Card, CardContent, Chip, Typography, makeStyles } from '@material-ui/core'
import DescriptionIcon from '@material-ui/icons/Description'
import ImageIcon from '@material-ui/icons/Image'
import VideoIcon from '@material-ui/icons/PlayCircleOutline'
import LinkIcon from '@material-ui/icons/Link'
import { audienceCodesFromState } from './wizardReducer'

const ICON_BY_TYPE = {
	document: DescriptionIcon,
	image: ImageIcon,
	video: VideoIcon,
	link: LinkIcon
}

const useStyles = makeStyles((theme) => ({
	heading: {
		fontWeight: 600,
		marginBottom: theme.spacing(2),
		color: theme.palette.text.primary
	},
	card: {
		marginBottom: theme.spacing(2)
	},
	cardHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(2),
		marginBottom: theme.spacing(1)
	},
	icon: {
		fontSize: 32
	},
	title: {
		fontWeight: 600
	},
	meta: {
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(0.5)
	},
	chips: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(0.5),
		marginTop: theme.spacing(1)
	},
	legend: {
		color: theme.palette.text.secondary,
		fontStyle: 'italic'
	}
}))

const Step3Confirm = ({ state }) => {
	const classes = useStyles()
	const Icon = ICON_BY_TYPE[state.type] || DescriptionIcon

	const categories = useSelector((s) => s.contentAssetCategories.get('categories').toJS())

	const categoryLabel = useMemo(() => {
		const byId = categories.reduce((acc, c) => ({ ...acc, [c.Id]: c }), {})
		const cat = byId[state.categoryId]
		if (!cat) return ''
		const chain = []
		let cursor = cat
		const seen = new Set()
		while (cursor && !seen.has(cursor.Id)) {
			seen.add(cursor.Id)
			chain.unshift(cursor.Name)
			cursor = cursor.ParentId ? byId[cursor.ParentId] : null
		}
		return chain.join(' › ')
	}, [categories, state.categoryId])

	const audienceCodes = audienceCodesFromState(state)

	return (
		<>
			<Typography variant='subtitle1' className={classes.heading}>
				Review before publishing
			</Typography>

			<Card variant='outlined' className={classes.card}>
				<CardContent>
					<div className={classes.cardHeader}>
						<Icon className={classes.icon} color='primary' />
						<div>
							<Typography variant='h6' className={classes.title}>
								{state.title}
							</Typography>
							{state.type === 'link' ? (
								<Typography variant='body2' className={classes.meta}>
									Link · {state.externalUrl}
								</Typography>
							) : (
								<Typography variant='body2' className={classes.meta}>
									{state.type ? state.type.charAt(0).toUpperCase() + state.type.slice(1) : ''}
									{state.file ? ` · ${state.file.name}` : ''}
								</Typography>
							)}
						</div>
					</div>

					<Typography variant='body2' className={classes.meta}>
						Category: <strong>{categoryLabel}</strong>
					</Typography>

					<Typography variant='body2' className={classes.meta}>
						Share with: <strong>{audienceCodes.join(', ')}</strong>
					</Typography>

					{state.tags.length > 0 && (
						<div className={classes.chips}>
							{state.tags.map((t) => (
								<Chip key={t.Id} label={t.Name} size='small' />
							))}
						</div>
					)}
				</CardContent>
			</Card>

			<Typography variant='caption' className={classes.legend}>
				The resource will be available to all authorized users in view-only mode. They will not be able to download it.
			</Typography>
		</>
	)
}

Step3Confirm.propTypes = {
	state: propTypes.object.isRequired
}

export default Step3Confirm
